import axios, { AxiosInstance } from 'axios';
import { User, Project, AuthResponse, PaginatedResponse, MailSettings } from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle responses
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<AuthResponse> {
    const { data } = await this.api.post('/auth/login', { email, password });
    return data;
  }

  async register(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role?: string,
    ward?: string
  ): Promise<AuthResponse> {
    const { data } = await this.api.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
      role,
      ward,
    });
    return data;
  }

  async refreshToken(): Promise<{ success: boolean; token: string }> {
    const { data } = await this.api.get('/auth/refresh-token');
    return data;
  }

  // Project endpoints
  async getProjects(
    page: number = 1,
    limit: number = 10,
    filters: any = {}
  ): Promise<PaginatedResponse<Project>> {
    const { data } = await this.api.get('/projects', {
      params: { page, limit, ...filters },
    });
    return data;
  }

  async getProjectById(id: string): Promise<{ success: boolean; data: Project }> {
    const { data } = await this.api.get(`/projects/${id}`);
    return data;
  }

  async getProjectByCode(code: string): Promise<{ success: boolean; data: Project }> {
    const { data } = await this.api.get(`/projects/code/${code}`);
    return data;
  }

  async createProject(project: Partial<Project>): Promise<{ success: boolean; data: Project }> {
    const { data } = await this.api.post('/projects', project);
    return data;
  }

  async updateProject(
    id: string,
    project: Partial<Project>
  ): Promise<{ success: boolean; data: Project }> {
    const { data } = await this.api.put(`/projects/${id}`, project);
    return data;
  }

  async deleteProject(id: string): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.delete(`/projects/${id}`);
    return data;
  }

  // Ward endpoints
  async getWards(): Promise<{ success: boolean; data: string[] }> {
    const { data } = await this.api.get('/wards');
    return data;
  }

  // File upload
  async uploadFile(file: File): Promise<{ success: boolean; data: { fileUrl: string } }> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await this.api.post('/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }

  // Feedback
  async submitFeedback(feedback: any): Promise<{ success: boolean; data: any }> {
    const { data } = await this.api.post('/feedback', feedback);
    return data;
  }

  // Users
  async getUsers(role?: string): Promise<{ success: boolean; data: User[] }> {
    const { data } = await this.api.get('/users', {
      params: role ? { role } : undefined,
    });
    return data;
  }

  async getCurrentUser(): Promise<{ success: boolean; data: User }> {
    const { data } = await this.api.get('/users/me');
    return data;
  }

  async updateCurrentUser(payload: Partial<User>): Promise<{ success: boolean; data: User }> {
    const { data } = await this.api.put('/users/me', payload);
    return data;
  }

  // Mail settings (admin)
  async getMailSettings(): Promise<{ success: boolean; data: MailSettings | null }> {
    const { data } = await this.api.get('/mail-settings');
    return data;
  }

  async updateMailSettings(payload: MailSettings): Promise<{ success: boolean; message: string; data: MailSettings }> {
    const { data } = await this.api.put('/mail-settings', payload);
    return data;
  }

  async sendMailTest(testEmail?: string): Promise<{ success: boolean; message: string }> {
    const { data } = await this.api.post('/mail-settings/test', testEmail ? { testEmail } : {});
    return data;
  }

  // Generic HTTP methods for flexibility
  async get(url: string, config?: any): Promise<any> {
    const { data } = await this.api.get(url, config);
    return data;
  }

  async post(url: string, payload?: any, config?: any): Promise<any> {
    const { data } = await this.api.post(url, payload, config);
    return data;
  }

  async put(url: string, payload?: any, config?: any): Promise<any> {
    const { data } = await this.api.put(url, payload, config);
    return data;
  }

  async delete(url: string, config?: any): Promise<any> {
    const { data } = await this.api.delete(url, config);
    return data;
  }
}

const apiService = new ApiService();
export default apiService;
